import { apiErrorSchema } from '@acasa/shared'
import type { z } from 'zod'

/**
 * Empty in dev — the vite proxy forwards /api to the API server, which keeps
 * the browser same-origin. Set VITE_API_URL when the two are deployed apart.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? ''

export const API_PREFIX = `${BASE_URL}/api/v1`

/** Error carrying the API's structured `code`, so callers can branch on it. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown }

/**
 * Single fetch wrapper for the whole app: prefixes the base URL, unwraps the
 * error envelope, and validates the payload against the shared schema so a
 * backend change surfaces here rather than three components deep.
 */
export async function apiFetch<TSchema extends z.ZodType>(
  path: string,
  schema: TSchema,
  { body, headers, ...init }: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    // The session lives in an httpOnly cookie. Same-origin would send it
    // anyway; `include` is what keeps it working if VITE_API_URL ever points
    // the app at a separate API origin.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload)
    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.error.code : 'unknown_error',
      parsed.success ? parsed.data.error.message : `Request to ${path} failed (${response.status})`,
      parsed.success ? parsed.data.error.details : payload,
    )
  }

  const result = schema.safeParse(payload)

  if (!result.success) {
    throw new ApiRequestError(
      response.status,
      'invalid_response',
      `Response from ${path} did not match the expected shape`,
      result.error.issues,
    )
  }

  return result.data
}
